// Parses a hand-written .md file into the article form's fields + content
// blocks, so an article can be written once as markdown and imported instead
// of clicked together block-by-block. Deliberately NOT a full Markdown
// renderer — only the subset our block schema supports (heading/subheading/
// paragraph/list/code/table/image/callout).

// --- frontmatter (--- key: value --- at the top) ---
function splitFrontmatter(raw) {
  const text = raw.replace(/\r\n/g, "\n").trim();
  if (!text.startsWith("---")) return { meta: {}, body: text };
  const end = text.indexOf("\n---", 3);
  if (end === -1) return { meta: {}, body: text };
  const block = text.slice(3, end).trim();
  const body = text.slice(end + 4).trim();
  const meta = {};
  block.split("\n").forEach((line) => {
    const idx = line.indexOf(":");
    if (idx === -1) return;
    const key = line.slice(0, idx).trim();
    const value = line.slice(idx + 1).trim();
    meta[key] = key === "tags" ? value.split(",").map((t) => t.trim()).filter(Boolean) : value;
  });
  return { meta, body };
}

const splitRow = (line) =>
  line.trim().replace(/^\|/, "").replace(/\|$/, "").split("|").map((c) => c.trim());

const isTableSeparator = (line) =>
  /^\s*\|?\s*:?-+:?\s*(\|\s*:?-+:?\s*)*\|?\s*$/.test(line || "");

function parseBody(body) {
  const lines = body.split("\n");
  const blocks = [];
  let paragraphBuf = [];
  let i = 0;

  const flushParagraph = () => {
    const text = paragraphBuf.join(" ").trim();
    if (text) blocks.push({ type: "paragraph", text });
    paragraphBuf = [];
  };

  while (i < lines.length) {
    const line = lines[i];

    if (line.trim() === "") {
      flushParagraph();
      i++;
      continue;
    }

    if (line.startsWith("```")) {
      flushParagraph();
      const codeLines = [];
      i++;
      while (i < lines.length && !lines[i].startsWith("```")) {
        codeLines.push(lines[i]);
        i++;
      }
      i++;
      blocks.push({ type: "code", text: codeLines.join("\n") });
      continue;
    }

    if (/^###\s+/.test(line)) {
      flushParagraph();
      blocks.push({ type: "subheading", text: line.replace(/^###\s+/, "").trim() });
      i++;
      continue;
    }
    if (/^##\s+/.test(line)) {
      flushParagraph();
      blocks.push({ type: "heading", text: line.replace(/^##\s+/, "").trim() });
      i++;
      continue;
    }
    if (/^#\s+/.test(line)) {
      flushParagraph();
      blocks.push({ type: "heading", text: line.replace(/^#\s+/, "").trim() });
      i++;
      continue;
    }

    if (/^>\s?/.test(line)) {
      flushParagraph();
      const quoteLines = [];
      while (i < lines.length && /^>\s?/.test(lines[i])) {
        quoteLines.push(lines[i].replace(/^>\s?/, ""));
        i++;
      }
      blocks.push({ type: "callout", text: quoteLines.join(" ").trim() });
      continue;
    }

    const imgMatch = line.match(/^!\[([^\]]*)\]\(([^)]+)\)\s*$/);
    if (imgMatch) {
      flushParagraph();
      blocks.push({ type: "image", caption: imgMatch[1], url: imgMatch[2] });
      i++;
      continue;
    }

    if (line.trim().startsWith("|") && isTableSeparator(lines[i + 1])) {
      flushParagraph();
      const headers = splitRow(line);
      i += 2;
      const rows = [];
      while (i < lines.length && lines[i].trim().startsWith("|")) {
        rows.push({ cells: splitRow(lines[i]) });
        i++;
      }
      blocks.push({ type: "table", headers, rows });
      continue;
    }

    if (/^\s*[-*]\s+/.test(line) || /^\s*\d+\.\s+/.test(line)) {
      flushParagraph();
      const items = [];
      while (i < lines.length && (/^\s*[-*]\s+/.test(lines[i]) || /^\s*\d+\.\s+/.test(lines[i]))) {
        items.push(lines[i].replace(/^\s*([-*]|\d+\.)\s+/, "").trim());
        i++;
      }
      blocks.push({ type: "list", items });
      continue;
    }

    paragraphBuf.push(line.trim());
    i++;
  }
  flushParagraph();
  return blocks;
}

export function parseArticleMarkdown(raw) {
  const { meta, body } = splitFrontmatter(raw);
  return { ...meta, content: parseBody(body) };
}

export const MARKDOWN_TEMPLATE = `---
title: Article title
slug: article-slug
excerpt: One or two sentences shown on the card and as the meta description.
author: Nodefleet Team
tags: Tag one, Tag two, Tag three
accent: #5AA9E6
readingTime: 5 min read
---

Everything above the blank line is metadata. Everything below is the body.

## A section heading

### An optional sub-heading

A normal paragraph. Just write text; blank lines start a new paragraph.

> A callout / highlighted note, written as a blockquote.

- A bullet list item
- Another item

\`\`\`
code or a terminal command goes in a fenced block like this
\`\`\`

| Column A | Column B |
| --- | --- |
| Row 1 value | Row 1 value |
| Row 2 value | Row 2 value |

![Optional caption](https://example.com/already-hosted-image.png)
`;
